import { useState } from "react";
import { Container, Typography, Box, Link, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CancelIcon from "@mui/icons-material/Cancel";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";

import { getPresignedUrlUploadCSV, uploadCSVPresignedUrl, processUploadedCSV } from "../../../services/apis";
import { AlertBar } from "../../../components";

export interface UploadWeatherAlert {
  showAlert: boolean;
  severity: string;
  title: string;
  description: string;
}

export default function UploadWeatherData() {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadWeatherAlert, setUploadWeatherAlert] = useState<UploadWeatherAlert>({
    showAlert: false,
    severity: "info",
    title: "",
    description: "",
  });

  const setShowAlert = (show: boolean) => {
    setUploadWeatherAlert({ ...uploadWeatherAlert, showAlert: show });
  };
  const handleFileSelect = (uploadedFile: File) => {
    if (uploadedFile.type !== "text/csv" && !uploadedFile.name.endsWith(".csv")) {
      alert("Only CSV files are allowed!");
      return;
    }
    if (uploadedFile.size > 3 * 1024 * 1024) {
      alert("File size must be less than 3MB!");
      return;
    }
    setFile(uploadedFile);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) handleFileSelect(uploadedFile);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const uploadedFile = event.dataTransfer.files[0];
    if (uploadedFile) handleFileSelect(uploadedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemoveFile = () => {
    setIsLoading(false);
    setFile(null);

    // Reset file input value so the same file can be selected again
    const input = document.getElementById("fileInput") as HTMLInputElement;
    if (input) {
      input.value = "";
    }
  };

  const handleGetPresignedUrlUploadCSV = async () => {
    setIsLoading(true);
    getPresignedUrlMutation.mutate({
      csv_upload: true,
      content_type: "csv",
    });
  };

  const getPresignedUrlMutation = useMutation({
    mutationFn: getPresignedUrlUploadCSV,
    onSuccess: (data) => {
      console.log("Presigned Url generated successfully:", data);
      handleUploadCSV(data);
    },
    onError: (error) => {
      console.error("Error Uploading csv file:", error);
      setIsLoading(false);
      setUploadWeatherAlert({
        showAlert: true,
        severity: "error",
        title: "File upload failed",
        description: "Please try again.",
      });
    },
  });

  const handleUploadCSV = async (data: any) => {
    console.log("Uploading CSV with presigned URL:", data);
    uploadCSVMutation.mutate({
      url: data?.pre_signed_url,
      file: file,
      s3_key: data?.s3_key,
    });
  };

  const uploadCSVMutation = useMutation({
    mutationFn: uploadCSVPresignedUrl,
    onSuccess: (data, variables) => {
      console.log("CSV uploaded successfully:", data, variables);
      handleProcessCSV(variables);
    },
    onError: (error) => {
      setIsLoading(false);
      console.error("Error Uploading csv file:", error);
      setUploadWeatherAlert({
        showAlert: true,
        severity: "error",
        title: "File upload failed",
        description: "Please try again.",
      });
    },
  });

  const handleProcessCSV = async (data: any) => {
    console.log("Uploading CSV with presigned URL:", data);
    processCSVMutation.mutate({
      csv_upload: false,
      s3_key: data?.s3_key,
    });
  };

  const processCSVMutation = useMutation({
    mutationFn: processUploadedCSV,
    onSuccess: (data) => {
      setIsLoading(false);
      handleRemoveFile();
      console.log("CSV processed successfully:", data);
      setUploadWeatherAlert({
        showAlert: true,
        severity: "success",
        title: "",
        description: "File uploaded",
      });
    },
    onError: (error) => {
      setIsLoading(false);
      console.error("Error Processing csv file:", error);
      setUploadWeatherAlert({
        showAlert: true,
        severity: "error",
        title: "File upload failed",
        description: "Please try again.",
      });
    },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "24px",
          position: "relative",
          borderRadius: "8px",
          marginTop: uploadWeatherAlert?.showAlert ? "24px" : "0",
          flexDirection: uploadWeatherAlert?.showAlert ? "column" : "row",
        }}
      >
        {uploadWeatherAlert?.showAlert && (
          <Box
            sx={{
              width: "368px",
              marginBottom: 2,
            }}
          >
            <AlertBar
              title={uploadWeatherAlert?.title}
              show={uploadWeatherAlert?.showAlert}
              description={uploadWeatherAlert?.description}
              severity={uploadWeatherAlert?.severity}
              setShow={setShowAlert}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "368px",
            padding: 2,
            boxShadow: 3,
            borderRadius: "8px",
            [theme.breakpoints.down("sm")]: {
              boxShadow: "none",
              width: "100%",
              paddingTop: 2,
            },
          }}
        >
          <Typography variant="h5" gutterBottom>
            Upload weather data
          </Typography>
          {!file ? (
            <Box
              role="button"
              aria-label="Upload CSV file"
              sx={{
                height: "240px",
                marginTop: "20px",
                border: dragOver ? "2px dashed #1976d2" : "2px dashed #E2E9F4",
                borderRadius: 2,
                p: 4,
                alignContent: "center",
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "#1976d2",
                },
              }}
              onClick={() => document.getElementById("fileInput")?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <UploadFileIcon />
              <Typography variant="body1">
                <Link component="button" underline="hover">
                  Click to browse
                </Link>{" "}
                or drag and drop
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CSV (max. 3MB)
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: "240px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                border: "1px solid #eee",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <UploadFileIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2">{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} kb
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={handleRemoveFile}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <input id="fileInput" type="file" accept=".csv" style={{ display: "none" }} onChange={handleFileChange} />
          <LoadingButton disabled={!file} loading={isLoading} variant="contained" onClick={handleGetPresignedUrlUploadCSV} sx={{ mt: "31px" }}>
            UPLOAD FILE
          </LoadingButton>
        </Box>
      </Container>
    </Box>
  );
}
